import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useResolveMarketingUsers, useSearchMarketingUsers } from '../../queries/marketingSegments';

interface ManualUserPickerProps {
  userIds: string[];
  onChange: (userIds: string[]) => void;
}

const label = (user: { display_name: string | null; email: string | null; cognito_id: string }) =>
  user.display_name || user.email || user.cognito_id;

export function ManualUserPicker({ userIds, onChange }: ManualUserPickerProps) {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(input), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const { data: results = [], isFetching, isError } = useSearchMarketingUsers(debounced);
  // Chips used to show the raw cognito id, so a segment's hand-picked members
  // were unidentifiable - including when editing one someone else built.
  const { data: resolved = [] } = useResolveMarketingUsers(userIds);
  const byId = new Map(resolved.map((u) => [u.cognito_id, u]));

  const searching = debounced.trim().length >= 2;

  const add = (cognitoId: string) => {
    if (!userIds.includes(cognitoId)) onChange([...userIds, cognitoId]);
    setInput('');
    setDebounced('');
  };

  const remove = (cognitoId: string) => onChange(userIds.filter((id) => id !== cognitoId));

  return (
    <div>
      <div className="relative">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search by name or email to add manually…"
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        />
        {searching && (
          <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-border bg-background shadow-lg">
            {/* Every outcome says something. Previously the list simply did not
                appear while searching, when nothing matched, or on an error,
                so a search that found nobody was indistinguishable from one
                that had not run. */}
            {isFetching && (
              <p className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching…
              </p>
            )}
            {!isFetching && isError && <p className="px-3 py-1.5 text-sm text-destructive">Search failed. Try again.</p>}
            {!isFetching && !isError && results.length === 0 && (
              <p className="px-3 py-1.5 text-sm text-muted-foreground">No users match “{debounced.trim()}”.</p>
            )}
            {!isFetching &&
              results.map((user) => (
                <button
                  key={user.cognito_id}
                  type="button"
                  onClick={() => add(user.cognito_id)}
                  disabled={userIds.includes(user.cognito_id)}
                  className="block w-full px-3 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-40"
                >
                  {label(user)}
                  {user.email && user.display_name && <span className="text-muted-foreground"> ({user.email})</span>}
                  {userIds.includes(user.cognito_id) && <span className="text-muted-foreground"> — already added</span>}
                </button>
              ))}
          </div>
        )}
      </div>
      {userIds.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {userIds.map((id) => {
            const user = byId.get(id);
            return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                <span title={user?.email ?? id}>{user ? label(user) : id}</span>
                <button type="button" onClick={() => remove(id)} aria-label={`Remove ${user ? label(user) : id}`} className="hover:text-red-500">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {userIds.length > 0 && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {userIds.length} user{userIds.length === 1 ? '' : 's'} added by hand — they are included regardless of the conditions above.
        </p>
      )}
    </div>
  );
}
